import * as React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import { CustomerCreateDTO } from '../generated/api-client';
import { useCustomer, useCreateCustomer, useUpdateCustomer } from '../hooks';
import { customerFormSchema, CustomerFormData } from '../schemas/customerFormSchema';
import {
  Box,
  Typography,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import HomeIcon from '@mui/icons-material/Home';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AddIcon from '@mui/icons-material/Add';
import { PageLayout } from '../components/layout';
import { ActionButton, TabButton } from '../components/common';
import { CustomerDetailMainTab } from '../components/customer/CustomerDetailMainTab';
import { CustomerDetailSitesTab } from '../components/customer/CustomerDetailSitesTab';

export const CustomerDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isNewCustomer = id === 'new';
  const [activeTab, setActiveTab] = React.useState<string>('main');
  const [showConfirmSaveDialog, setShowConfirmSaveDialog] = React.useState(false);
  const mainFormMethods = useForm<CustomerFormData>({
    resolver: zodResolver(customerFormSchema),
    defaultValues: {
      company_name: '',
      company_name_type: '',
      ateco_2025: '',
      address: '',
      phone: '',
      voluntary: null,
      type: undefined,
      multi_site: undefined,
      clustering: undefined,
      employee_count: null,
      revenue: null,
      balance_sheet_assets: null,
      size_category: undefined,
      consumption_category: undefined,
      average_annual_consumption: null,
      high_electricity_consumption: undefined,
      high_gas_consumption: undefined,
      certifications: '',
      vat_number: '',
      tax_code: '',
      certified_email: '',
      legal_representative: '',
      legal_representative_phone: '',
      legal_representative_email: '',
      contact_person: '',
      contact_person_phone: '',
      contact_person_email: '',
    },
    mode: 'onChange',
  });

  // Fetch customer data (skip if new customer)
  const { data: customer, isLoading } = useCustomer(isNewCustomer ? '' : id || '');

  // Initialize main form when customer data is loaded
  React.useEffect(() => {
    if (customer) {
      const formData: CustomerFormData = {
        company_name: customer.company_name || '',
        company_name_type: (customer as { company_name_type?: string }).company_name_type ?? '',
        ateco_2025: (customer.ateco_2025 as unknown as string) || '',
        address: (customer.address as unknown as string) || '',
        phone: (customer.phone as unknown as string) || '',
        voluntary: customer.voluntary ?? null,
        type: (customer.type as unknown as string | null) ?? undefined,
        multi_site: customer.multi_site ?? null,
        clustering: (customer.clustering as unknown as string | null) ?? undefined,
        employee_count: (customer.employee_count as unknown as number | null) ?? null,
        revenue: (customer.revenue as unknown as number | null) ?? null,
        balance_sheet_assets: (customer.balance_sheet_assets as unknown as number | null) ?? null,
        size_category: (customer.size_category as unknown as string | null) ?? undefined,
        consumption_category:
          (customer.consumption_category as unknown as string | null) ?? undefined,
        average_annual_consumption:
          (customer.average_annual_consumption as unknown as number | null) ?? null,
        high_electricity_consumption: customer.high_electricity_consumption ?? undefined,
        high_gas_consumption: customer.high_gas_consumption ?? undefined,
        certifications: (customer.certifications as unknown as string) || '',
        vat_number: (customer.vat_number as unknown as string) || '',
        tax_code: (customer.tax_code as unknown as string) || '',
        certified_email: (customer.certified_email as unknown as string) || '',
        legal_representative: (customer.legal_representative as unknown as string) || '',
        legal_representative_phone:
          (customer.legal_representative_phone as unknown as string) || '',
        legal_representative_email:
          (customer.legal_representative_email as unknown as string) || '',
        contact_person: (customer.contact_person as unknown as string) || '',
        contact_person_phone: (customer.contact_person_phone as unknown as string) || '',
        contact_person_email: (customer.contact_person_email as unknown as string) || '',
      };
      mainFormMethods.reset(formData, { keepDefaultValues: false, keepDirty: false });
    }
  }, [customer, mainFormMethods]);

  // Determine which tab to show based on path
  const getTabFromPath = (path: string): string => {
    if (path.includes('/siti')) {
      return 'sites';
    }
    return 'main';
  };

  // Update active tab when path changes
  React.useEffect(() => {
    const tabId = getTabFromPath(location.pathname);
    setActiveTab(tabId);

    if (!isNewCustomer && location.pathname.includes('/company-data') && id) {
      navigate(`/customers/${id}`, { replace: true });
    }
  }, [location.pathname, isNewCustomer, navigate, id]);

  // Define fixed tabs with icons (only main tab for new customers)
  const fixedTabs = isNewCustomer
    ? []
    : [
        { id: 'main', label: 'Principale', path: `/customers/${id}`, icon: <HomeIcon /> },
        { id: 'sites', label: 'Siti', path: `/customers/${id}/siti`, icon: <LocationOnIcon /> },
      ];

  // Save main form mutation
  const updateCustomerMutation = useUpdateCustomer();
  const createCustomerMutation = useCreateCustomer();

  const saveMainMutation = {
    mutate: (data: CustomerFormData) => {
      if (isNewCustomer) {
        // Create new customer
        createCustomerMutation.mutate(data as CustomerCreateDTO, {
          onSuccess: newCustomer => {
            toast.success('Customer creato con successo');
            // Redirect to the new customer's detail page
            navigate(`/customers/${newCustomer.id}`, { replace: true });
          },
          onError: (error: any) => {
            toast.error(error?.message || 'Errore durante la creazione del customer');
          },
        });
      } else {
        // Update existing customer
        if (!id) throw new Error('Customer ID is required');
        updateCustomerMutation.mutate(
          { id, data: data as CustomerCreateDTO },
          {
            onSuccess: () => {
              toast.success('Cliente aggiornato con successo');
              const savedValues = mainFormMethods.getValues();
              mainFormMethods.reset(savedValues, { keepDefaultValues: false, keepDirty: false });
            },
            onError: (error: any) => {
              toast.error(error?.message || "Errore durante l'aggiornamento del customer");
            },
          }
        );
      }
    },
    isLoading: updateCustomerMutation.isLoading || createCustomerMutation.isLoading,
  };

  const handleSave = async () => {
    if (activeTab !== 'main') {
      return;
    }

    const isValid = await mainFormMethods.trigger();
    if (!isValid) {
      const errors = mainFormMethods.formState.errors;
      const hasErrors = Object.keys(errors).length > 0;

      if (hasErrors) {
        const hasOnlyRequiredErrors = Object.values(errors).every(error => {
          if (!error) return false;
          const message = error.message;
          if (typeof message === 'string') {
            return message.includes('obbligatori');
          }
          if (Array.isArray(message)) {
            return message.every(m => typeof m === 'string' && m.includes('obbligatori'));
          }
          return false;
        });

        if (hasOnlyRequiredErrors) {
          setShowConfirmSaveDialog(true);
        }
        return;
      }
    }

    mainFormMethods.handleSubmit(data => {
      saveMainMutation.mutate(data as any);
    })();
  };

  const handleConfirmSave = () => {
    const values = mainFormMethods.getValues();
    saveMainMutation.mutate(values as any);
    setShowConfirmSaveDialog(false);
  };

  const handleCancelSave = () => {
    setShowConfirmSaveDialog(false);
  };

  const handleTabChange = (tabId: string, path: string) => {
    setActiveTab(tabId);
    navigate(path);
  };

  const handleAddSite = () => {
    // Navigate to new site page with customer_id as query param
    navigate(`/sites/new?customer_id=${id}`);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'main':
        return (
          <Box sx={{ p: 2 }}>
            <FormProvider {...mainFormMethods}>
              <CustomerDetailMainTab />
            </FormProvider>
          </Box>
        );
      case 'sites':
        return <CustomerDetailSitesTab customerId={id || ''} />;
      default:
        return null;
    }
  };

  const hasUnsavedChanges = mainFormMethods.formState.isDirty && activeTab === 'main';

  if (isLoading && !isNewCustomer) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Caricamento...</Typography>
      </Box>
    );
  }

  if (!customer && !isNewCustomer) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">Customer non trovato</Typography>
      </Box>
    );
  }

  return (
    <>
      <PageLayout
        header={
          <>
            <BusinessIcon fontSize="large" />
            <Typography variant="h5" sx={{ marginLeft: '1rem' }}>
              {isNewCustomer ? 'Nuovo Customer' : customer?.company_name}
            </Typography>
          </>
        }
        actions={
          <>
            {/* Tab buttons */}
            {!isNewCustomer && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {fixedTabs.map(tab => (
                  <TabButton
                    key={tab.id}
                    icon={tab.icon}
                    label={tab.label}
                    active={activeTab === tab.id}
                    onClick={() => handleTabChange(tab.id, tab.path)}
                  />
                ))}
              </Box>
            )}

            {!isNewCustomer && (
              <Divider
                orientation="vertical"
                variant="middle"
                flexItem
                sx={{ mx: 1, borderColor: 'divider' }}
              />
            )}

            {/* Add Site button - only visible on sites tab */}
            {activeTab === 'sites' ? (
              <ActionButton
                icon={<AddIcon />}
                tooltip="Aggiungi nuovo sito"
                onClick={handleAddSite}
              />
            ) : (
              <ActionButton
                icon={<SaveIcon />}
                tooltip={hasUnsavedChanges ? 'Salva modifiche' : 'Nessuna modifica da salvare'}
                onClick={handleSave}
                disabled={!hasUnsavedChanges}
                loading={saveMainMutation.isLoading}
              />
            )}
          </>
        }
      >
        {renderTabContent()}
      </PageLayout>

      <Dialog open={showConfirmSaveDialog} onClose={handleCancelSave}>
        <DialogTitle>Conferma salvataggio dati</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Ci sono alcuni campi obbligatori non compilati, vuoi procedere ugualmente al
            salvataggio?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Box sx={{ pb: 2, pr: 2 }}>
            <Button onClick={handleCancelSave} color="inherit">
              Annulla
            </Button>
            <Button sx={{ ml: 2 }} onClick={handleConfirmSave} color="primary" variant="contained">
              Salva comunque
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};
